import type { BudgetForecastDataItem } from 'lumen/models';

export function itemHasChildWithDataForHiddenSegment(
  item: BudgetForecastDataItem,
): boolean {
  return item.filteredChildren.some(
    (child) => child.isItemForHiddenSegment && child.hasData,
  );
}

export function itemHasUnmappedChildWithData(
  item: BudgetForecastDataItem,
): boolean {
  return item.filteredChildren.some(
    (child) => child.isUnmapped && child.hasData,
  );
}

export function itemHasDataAndAllChildrenHaveNoData(
  item: BudgetForecastDataItem,
): boolean {
  return (
    item.hasData &&
    item.filteredChildren.length > 0 &&
    item.filteredChildren.every((child) => !child.hasData)
  );
}

function itemNeedsForceDistribution(item: BudgetForecastDataItem): boolean {
  return (
    itemHasChildWithDataForHiddenSegment(item) ||
    itemHasUnmappedChildWithData(item) ||
    itemHasDataAndAllChildrenHaveNoData(item)
  );
}

/**
 * BFS search for items that need to be force distributed.
 *
 * e.g. the following tree should return 'Transient' and 'Group'
 * - Root
 * | - Transient
 * |   | - OTA
 * |   | - Consortia
 * |   | - Retail (Hidden)
 * | - Group
 * |   | - Government
 * |   | - Leisure (Hidden)
 *
 * e.g. the following tree should return 'Root'
 * - Root
 * | - Transient
 * |   | - OTA
 * |   | - Consortia
 * |   | - Retail (Hidden)
 * | - Group (Hidden)
 */
function _getItemsToForceDistribute(
  item: BudgetForecastDataItem,
  itemsToForceDistribute: BudgetForecastDataItem[] = [],
): BudgetForecastDataItem[] {
  if (itemNeedsForceDistribution(item)) {
    itemsToForceDistribute.push(item);
    return itemsToForceDistribute;
  }

  item.filteredChildren.forEach((child) => {
    _getItemsToForceDistribute(child, itemsToForceDistribute);
  });

  return itemsToForceDistribute;
}

export function markItemsAsNeedsForceDistribution(
  items: BudgetForecastDataItem[],
): void {
  items
    .filter((item) => item.isRoot)
    .flatMap((item) => _getItemsToForceDistribute(item))
    .forEach((item) => (item.needsForceDistribution = true));
}
